import React from 'react';

// ─── Formatting helpers ───────────────────────────────────────────────────────
const fmtDate = (d) => {
  if (!d) return '-';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return String(d);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${String(dt.getDate()).padStart(2, '0')} ${months[dt.getMonth()]} ${dt.getFullYear()}`;
};

const getValidityDate = (e) => {
  if (e?.validity_date) return fmtDate(e.validity_date);
  if (e?.validity_notes) {
    const dateMatch = e.validity_notes.match(/\b\d{4}-\d{2}-\d{2}\b/) || e.validity_notes.match(/\b\d{2}[/-]\d{2}[/-]\d{4}\b/);
    if (dateMatch) return fmtDate(dateMatch[0]);
    const daysMatch = e.validity_notes.match(/(\d+)\s*days?/i);
    if (daysMatch && daysMatch[1] && e.estimate_date) {
      const dt = new Date(e.estimate_date);
      if (!isNaN(dt.getTime())) {
        dt.setDate(dt.getDate() + parseInt(daysMatch[1], 10));
        return fmtDate(dt);
      }
    }
    if (e.validity_notes.length <= 25) return e.validity_notes;
  }
  if (e?.estimate_date) {
    const dt = new Date(e.estimate_date);
    if (!isNaN(dt.getTime())) {
      dt.setDate(dt.getDate() + 7);
      return fmtDate(dt);
    }
  }
  return '-';
};

const fmtAmt = (v, curr = '₹') => {
  if (v === null || v === undefined || v === '') return '-';
  const n = parseFloat(v);
  if (isNaN(n)) return '-';
  return `${curr} ${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmtNum = (v, decimals = 2) => {
  if (v === null || v === undefined || v === '') return '-';
  const n = parseFloat(v);
  if (isNaN(n)) return '-';
  return n % 1 === 0 ? String(n) : n.toFixed(decimals);
};

const formatTat = (tat) => {
  if (!tat || !String(tat).trim()) return '-';
  const s = String(tat).trim();
  if (/hours?|hrs?|days?/i.test(s)) return s;
  const num = parseFloat(s);
  if (!isNaN(num)) {
    return num >= 12 ? `${num} Hours` : `${num} Day${num > 1 ? 's' : ''}`;
  }
  return s;
};

const getCleanLogoUrl = (url) => {
  if (!url) return null;
  const match = url.match(/id=([^&]+)/) || url.match(/\/d\/([^/]+)/);
  if (match && match[1]) {
    return `https://drive.google.com/thumbnail?id=${match[1]}&sz=w500`;
  }
  return url;
};

const numberToWordsINR = (amount, currency = 'INR') => {
  if (!amount || isNaN(amount)) return '';
  const a = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
    'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  const convert = (num) => {
    if (num === 0) return '';
    let str = '';
    if (num >= 10000000) {
      str += convert(Math.floor(num / 10000000)) + ' Crore ';
      num %= 10000000;
    }
    if (num >= 100000) {
      str += convert(Math.floor(num / 100000)) + ' Lakh ';
      num %= 100000;
    }
    if (num >= 1000) {
      str += convert(Math.floor(num / 1000)) + ' Thousand ';
      num %= 1000;
    }
    if (num >= 100) {
      str += convert(Math.floor(num / 100)) + ' Hundred ';
      num %= 100;
    }
    if (num > 0) {
      if (str !== '') str += 'and ';
      if (num < 20) str += a[num] + ' ';
      else str += b[Math.floor(num / 10)] + (num % 10 ? ' ' + a[num % 10] : '') + ' ';
    }
    return str.trim();
  };

  const whole = Math.floor(Math.abs(amount));
  const fraction = Math.round((Math.abs(amount) - whole) * 100);
  const prefix = currency === 'INR' ? 'Rupees ' : `${currency} `;
  let words = prefix + convert(whole);
  if (fraction > 0) {
    words += ` and ${convert(fraction)} Paise`;
  }
  return words + ' Only';
};

export const PRINT_STYLES = `
  @page {
    size: A4 portrait;
    margin: 8mm 10mm 10mm 10mm;
  }
  @media print {
    html, body {
      margin: 0 !important;
      padding: 0 !important;
      background: #fff !important;
      font-size: 12px !important;
      font-family: Arial, sans-serif !important;
      height: auto !important;
      min-height: 0 !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    .no-print,
    .no-print *,
    [data-no-print="true"],
    [data-no-print="true"] *,
    .print-actions,
    .print-actions *,
    .floating-toolbar,
    .floating-toolbar *,
    .print\:hidden {
      display: none !important;
      visibility: hidden !important;
      opacity: 0 !important;
      height: 0 !important;
      width: 0 !important;
      position: absolute !important;
      left: -99999px !important;
      top: -99999px !important;
    }
    .page-break { page-break-before: always; }
    .avoid-break { break-inside: avoid; page-break-inside: avoid; }
    .print-container { width: 100% !important; margin: 0 !important; padding: 0 !important; box-shadow: none !important; }
  }
  @media screen {
    body { background: #f3f4f6; }
    .print-container {
      max-width: 880px;
      margin: 20px auto 50px auto;
      background: #fff;
      padding: 28px 32px;
      box-shadow: 0 4px 20px rgba(0,0,0,0.08);
      border-radius: 8px;
    }
  }
  * { box-sizing: border-box; }
  body {
    font-family: Arial, sans-serif !important;
    font-size: 12px;
    line-height: 1.45;
    color: #1e293b;
    margin: 0;
  }
  table { width: 100%; border-collapse: collapse; }
  th, td { padding: 5px 7px; font-size: 12px; font-family: Arial, sans-serif; }
  .table-bordered th, .table-bordered td {
    border: 1px solid #94a3b8 !important;
    line-height: 1.4;
  }
  .table-bordered th {
    background: #f1f5f9;
    color: #0f172a;
    font-weight: 700;
    font-size: 12px;
    letter-spacing: 0.2px;
    padding: 6px 8px;
    font-family: Arial, sans-serif;
  }
  .table-bordered td {
    font-size: 12px;
    font-family: Arial, sans-serif;
  }
`;

export const formatEstimateWhatsAppText = (e, company) => {
  if (!e) return '';
  const cmpName = company?.name || 'STAVAN INTERNATIONAL LLP';
  const curr = e.currency === 'INR' ? '₹' : (e.currency || 'INR');
  const total = Number(e.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const origin = e.aol || e.road_from || e.pickup_address || '-';
  const destination = e.aod || e.road_to || e.delivery_at || e.road_location || '-';
  const dateStr = e.estimate_date ? String(e.estimate_date).split('T')[0] : '';
  const weight = e.chargeable_weight ? `${e.chargeable_weight} ${e.packages?.[0]?.weight_unit || 'KGS'}` : (e.gross_weight ? `${e.gross_weight} KGS` : '-');

  return `Dear *${e.party_name || 'Customer'}*,

Thank you for your inquiry. Here are the quotation details from *${cmpName}*:

📄 *Quotation No:* ${e.estimate_no || '-'}
📅 *Date:* ${dateStr}
✈️ *Mode & Scope:* ${e.mode_of_transport || 'Air'} (${e.shipment_scope || 'International'})
🔄 *Direction:* ${e.movement_direction || 'Inward'}
📍 *Origin:* ${origin}
🏁 *Destination:* ${destination}
📦 *Chargeable Weight:* ${weight}
💰 *Total Amount:* ${curr} ${total}

For the full detailed quotation breakdown with terms & conditions, please check your email or contact our operations desk.

Warm regards,
*${cmpName}*
📞 ${company?.phone || '+91-79-40088280'}
✉️ ${company?.email || 'admin@stavan.co.in'}`;
};

export const formatEstimateEmailHtml = (e, company) => {
  if (!e) return '';
  const cmpName = company?.name || 'STAVAN INTERNATIONAL LLP';
  const curr = e.currency === 'INR' ? '₹' : (e.currency || 'INR');
  const total = Number(e.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const origin = e.aol || e.road_from || e.pickup_address || '—';
  const destination = e.aod || e.road_to || e.delivery_at || e.road_location || '—';
  const dateStr = e.estimate_date ? String(e.estimate_date).split('T')[0] : '';

  return `<div style="font-family:Arial,sans-serif;max-width:650px;margin:0 auto;padding:20px;color:#1e293b;line-height:1.5">
<h2 style="color:#1e40af;margin-top:0;font-size:20px;border-bottom:2px solid #e2e8f0;padding-bottom:10px">Logistics Quotation</h2>
<p style="font-size:14px">Dear <strong>${e.party_name || 'Customer'}</strong>,</p>
<p style="font-size:13.5px">Thank you for your valuable inquiry. We are pleased to provide our official quotation for your upcoming consignment.</p>

<table style="width:100%;border-collapse:collapse;margin:18px 0;font-size:13px;border:1px solid #cbd5e1">
  <tr style="background:#f8fafc">
    <td style="padding:10px 14px;border:1px solid #e2e8f0;font-weight:bold;color:#475569;width:35%">Quotation No.</td>
    <td style="padding:10px 14px;border:1px solid #e2e8f0;font-weight:700;color:#1e40af;font-size:14px">${e.estimate_no || '—'}</td>
  </tr>
  <tr>
    <td style="padding:10px 14px;border:1px solid #e2e8f0;font-weight:bold;color:#475569">Quotation Date</td>
    <td style="padding:10px 14px;border:1px solid #e2e8f0">${dateStr}</td>
  </tr>
  <tr style="background:#f8fafc">
    <td style="padding:10px 14px;border:1px solid #e2e8f0;font-weight:bold;color:#475569">Shipment Mode &amp; Scope</td>
    <td style="padding:10px 14px;border:1px solid #e2e8f0">${e.mode_of_transport || 'AIR'} / ${e.shipment_scope || 'INTERNATIONAL'} (${e.movement_direction || 'INWARD'})</td>
  </tr>
  <tr>
    <td style="padding:10px 14px;border:1px solid #e2e8f0;font-weight:bold;color:#475569">Origin / Loading</td>
    <td style="padding:10px 14px;border:1px solid #e2e8f0">${origin}</td>
  </tr>
  <tr style="background:#f8fafc">
    <td style="padding:10px 14px;border:1px solid #e2e8f0;font-weight:bold;color:#475569">Destination / Delivery</td>
    <td style="padding:10px 14px;border:1px solid #e2e8f0">${destination}</td>
  </tr>
  ${e.chargeable_weight ? `<tr><td style="padding:10px 14px;border:1px solid #e2e8f0;font-weight:bold;color:#475569">Chargeable Weight</td><td style="padding:10px 14px;border:1px solid #e2e8f0">${e.chargeable_weight} ${e.packages?.[0]?.weight_unit || 'KGS'}</td></tr>` : ''}
  <tr style="background:#ecfdf5">
    <td style="padding:12px 14px;border:1px solid #a7f3d0;font-weight:bold;color:#065f46;font-size:14px">Total Quotation Amount</td>
    <td style="padding:12px 14px;border:1px solid #a7f3d0;font-weight:900;color:#047857;font-size:16px">${curr} ${total}</td>
  </tr>
</table>

<p style="font-size:13px;color:#334155">The complete official estimate with line-by-line charge breakdown, transit specifications, and standard terms is attached as a PDF file (<strong>${e.estimate_no}.pdf</strong>).</p>


<p style="font-size:13px">Please let us know if you require any modifications or wish to proceed with booking.</p>

<div style="margin-top:28px;padding-top:14px;border-top:1px solid #e2e8f0;font-size:12px;color:#64748b">
  <div style="font-weight:bold;color:#0f172a;font-size:13px">${cmpName}</div>
  <div>${company?.address || ''} ${company?.city ? company.city + ',' : ''} ${company?.state || ''}</div>
  <div>Phone: ${company?.phone || '+91-79-40088280'} • Email: ${company?.email || 'admin@stavan.co.in'} • Web: ${company?.website || 'www.stavan.co.in'}</div>
</div>
</div>`;
};

export default function EstimatePrintDocument({ estimate, company }) {
  if (!estimate) return null;
  const e = estimate;
  const CURRENCY_SYMBOLS = {
  INR: '₹',
  USD: '$',
  EUR: '€',
  GBP: '£',
  AUD: 'A$',
  AED: 'AED',
  SGD: 'S$',
  CNY: '¥',
  JPY: '¥',
  CAD: 'C$',
  CHF: 'CHF',
  HKD: 'HK$',
  SAR: 'SAR',
  MYR: 'RM',
  THB: '฿',
  KWD: 'KWD',
  OMR: 'OMR',
  QAR: 'QAR',
  BHD: 'BHD',
  NZD: 'NZ$',
};

const currSymbol = CURRENCY_SYMBOLS[e.currency] || (e.currency ? `${e.currency} ` : '₹');

  // Company details with fallbacks
  const cmpName = company?.name || 'STAVAN INTERNATIONAL LLP';
  const cmpAddr = [company?.address, company?.city, company?.state, company?.postal_code || company?.pincode, company?.country]
    .filter(Boolean).join(', ') || 'Reg. Off: 737-738, Sun Gravitas, Radio Mirchi Road, Vejalpur, Ahmedabad - 380051, Gujarat, India';
  const cmpPhone = company?.phone || '+91-79-40088280';
  const cmpEmail = company?.email || 'admin@stavan.co.in';
  const cmpWeb = company?.website || 'www.stavan.co.in';
  const cmpGst = company?.gst_number || '24ADXFS3875R1ZR';
  const cmpPan = company?.pan_number;

  const isLogisticsMode = e.shipment_scope === 'DOMESTIC' || e.mode_of_transport === 'ROAD' || e.mode_of_transport === 'RAIL';

  // Logistics metadata extraction
  let remarksList = [];
  let logisticsMeta = null;
  if (e.remarks_json) {
    try {
      const parsed = JSON.parse(e.remarks_json);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && parsed.is_logistics) {
        logisticsMeta = parsed;
        if (Array.isArray(parsed.remarks)) remarksList = parsed.remarks.filter(Boolean);
      } else if (Array.isArray(parsed)) {
        remarksList = parsed.filter(Boolean);
      }
    } catch {}
  }

  // Logistics items and tax computations
  const logisticsGroup = (e.charge_groups || [])[0];
  const logisticsItems = logisticsGroup?.items || [];
  const logSubTotal = logisticsMeta?.sub_total !== undefined 
    ? logisticsMeta.sub_total 
    : (parseFloat(logisticsGroup?.group_total) || logisticsItems.reduce((acc, it) => acc + (parseFloat(it.amount || it.total) || 0), 0));
  const logGstRate = logisticsMeta?.gst_rate !== undefined ? logisticsMeta.gst_rate : 5;
  const logGstLabel = logisticsMeta?.gst_label || (logGstRate > 0 ? `GST (${logGstRate}% GTA RCM/FCM)` : 'GST (0% Exempt)');
  const logGstAmount = logisticsMeta?.gst_amount !== undefined ? logisticsMeta.gst_amount : ((logSubTotal * logGstRate) / 100);
  const logTotalPayable = logisticsMeta?.total_payable !== undefined ? logisticsMeta.total_payable : (logSubTotal + logGstAmount);

  const grandTotal = isLogisticsMode 
    ? (logTotalPayable || parseFloat(e.total_amount) || 0) 
    : (e.charge_groups || []).reduce((s, g) => s + (parseFloat(g.group_total) || 0), 0);

  // Parse dimensions from dimensions_json or legacy packages
  let dimensionRows = [];
  if (e.dimensions_json) {
    try {
      const parsed = JSON.parse(e.dimensions_json);
      if (Array.isArray(parsed)) dimensionRows = parsed;
    } catch {}
  }
  if (dimensionRows.length === 0 && (e.packages || []).length > 0) {
    dimensionRows = e.packages.map(p => {
      const parts = (p.dimension || '').split('x');
      return {
        package_type: p.package_type || 'Boxes',
        packages: String(p.packages || p.no_of_packages || '1'),
        boxes: p.no_of_packages || 1,
        gross_weight: p.gross_weight !== undefined && p.gross_weight !== null ? String(p.gross_weight) : '',
        length: parts[0] || '',
        width: parts[1] || '',
        height: parts[2] || '',
        volume_weight: p.volume_weight || 0,
      };
    });
  }

  const isSeaMode = e.mode_of_transport === 'SEA';
  const isFCL = isSeaMode && e.sea_load_type === 'FCL';
  const isRoadRail = e.mode_of_transport === 'ROAD' || e.mode_of_transport === 'RAIL';

  return (
    <div className="print-container" style={{ background: '#fff', color: '#1e293b' }}>
      
      {/* ── 1. Elegant Header & Company Branding ──────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', paddingBottom: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {company?.logo_url ? (
            <img
              src={getCleanLogoUrl(company.logo_url)}
              alt={cmpName}
              referrerPolicy="no-referrer"
              crossOrigin="anonymous"
              style={{ maxHeight: '66px', maxWidth: '190px', objectFit: 'contain' }}
              onError={(ev) => {
                const match = company.logo_url.match(/id=([^&]+)/) || company.logo_url.match(/\/d\/([^/]+)/);
                if (match && match[1] && !ev.target.dataset.retried) {
                  ev.target.dataset.retried = 'true';
                  ev.target.src = `https://lh3.googleusercontent.com/d/${match[1]}=w400`;
                }
              }}
            />
          ) : (
            <div style={{
              background: '#0f172a',
              color: '#fff',
              width: '50px',
              height: '50px',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: '900',
              fontSize: '24px',
              letterSpacing: '1px'
            }}>
              {cmpName.charAt(0)}
            </div>
          )}
          <div>
            <div style={{
              fontSize: '19px',
              fontWeight: '800',
              color: '#0f172a',
              letterSpacing: '0.6px',
              lineHeight: 1.15,
              textTransform: 'uppercase'
            }}>
              {cmpName}
            </div>
            <div style={{
              fontSize: '12px',
              fontWeight: '600',
              letterSpacing: '1.5px',
              color: '#d97706',
              marginTop: '2px',
              textTransform: 'uppercase'
            }}>
              Global Freight Forwarding &amp; Logistics Solutions
            </div>
            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '1px' }}>
              Customs Broker • Reefer Transport • Air &amp; Ocean Cargo
            </div>
          </div>
        </div>

        {/* Big Badge: QUOTATION / ESTIMATE */}
        <div style={{ textAlign: 'right' }}>
          <div style={{
            fontSize: '24px',
            fontWeight: '900',
            letterSpacing: '2px',
            color: '#1e3a8a',
            textTransform: 'uppercase',
            lineHeight: 1.1
          }}>
            QUOTATION
          </div>
          <div style={{ fontSize: '12px', color: '#64748b', marginTop: '3px' }}>
            Ref: <strong style={{ color: '#0f172a' }}>{e.estimate_no || '-'}</strong>
          </div>
          <div style={{ fontSize: '12px', color: '#64748b' }}>
            Date: <strong style={{ color: '#0f172a' }}>{fmtDate(e.estimate_date)}</strong>
          </div>
          <div style={{ fontSize: '12px', color: '#64748b' }}>
            Validity Date: <strong style={{ color: '#0f172a' }}>{getValidityDate(e)}</strong>
          </div>
        </div>
      </div>

      {/* Decorative accent rule */}
      <div style={{
        height: '3px',
        background: 'linear-gradient(90deg, #1e3a8a 0%, #2563eb 50%, #f59e0b 100%)',
        marginBottom: '14px',
        borderRadius: '2px'
      }} />

      {/* ── 2. Two-Column Metadata Box: Quotation Prepared For vs Routing (13px Uniform) ── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '14px',
        marginBottom: '14px',
        fontSize: '12px'
      }}>
        {/* Customer / Party Block */}
        <div style={{
          border: '1px solid #cbd5e1',
          borderRadius: '6px',
          padding: '10px 14px',
          background: '#f8fafc',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between'
        }}>
          <div>
            <div style={{
              fontSize: '12px',
              fontWeight: '700',
              letterSpacing: '0.5px',
              color: '#1e3a8a',
              textTransform: 'uppercase',
              marginBottom: '6px',
              borderBottom: '1px solid #e2e8f0',
              paddingBottom: '3px'
            }}>
              Quotation Prepared For:
            </div>
            <div style={{ fontSize: '12px', fontWeight: '800', color: '#0f172a', textTransform: 'uppercase' }}>
              {e.party_name || '-'}
            </div>
            {e.place && (
              <div style={{ color: '#334155', fontSize: '12px', marginTop: '2px' }}>
                <strong>Place: </strong>{e.place}
              </div>
            )}
            {e.pickup_address && (
              <div style={{ color: '#475569', fontSize: '12px', marginTop: '2px', whiteSpace: 'pre-line' }}>
                <strong>Address: </strong>{e.pickup_address}
              </div>
            )}
            {e.attention && (
              <div style={{ color: '#334155', fontSize: '12px', marginTop: '4px' }}>
                <strong>Attn: </strong>{e.attention}
              </div>
            )}
            {e.po_number && (
              <div style={{ color: '#334155', fontSize: '12px', marginTop: '2px' }}>
                <strong>PO / Ref No: </strong>{e.po_number}
              </div>
            )}
          </div>
          {e.intro_remarks && (
            <div style={{ marginTop: '8px', paddingTop: '6px', borderTop: '1px dashed #cbd5e1', fontStyle: 'italic', color: '#475569', fontSize: '12px' }}>
              &ldquo;{e.intro_remarks}&rdquo;
            </div>
          )}
        </div>

        {/* Shipment Scope & Movement Info Block */}
        <div style={{
          border: '1px solid #cbd5e1',
          borderRadius: '6px',
          padding: '10px 14px',
          background: '#f8fafc'
        }}>
          <div style={{
            fontSize: '12px',
            fontWeight: '700',
            letterSpacing: '0.5px',
            color: '#1e3a8a',
            textTransform: 'uppercase',
            marginBottom: '6px',
            borderBottom: '1px solid #e2e8f0',
            paddingBottom: '3px'
          }}>
            Shipment Routing &amp; Specifications:
          </div>
          <table style={{ width: '100%', fontSize: '12px' }}>
            <tbody>
              {isLogisticsMode ? (
                <>
                  <tr>
                    <td style={{ color: '#64748b', padding: '2px 0', width: '38%' }}>Scope / Movement:</td>
                    <td style={{ fontWeight: '700', color: '#0f172a', padding: '2px 0' }}>
                      DOMESTIC LOGISTICS • {e.mode_of_transport || 'ROAD'} {e.movement_direction ? `• ${e.movement_direction}` : ''}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ color: '#64748b', padding: '2px 0' }}>Load Type:</td>
                    <td style={{ color: '#0369a1', padding: '2px 0', fontWeight: '700' }}>
                      {e.road_load_type === 'PTL' ? 'PTL — Part Truck Load' : 'FTL — Full Truck Load'}
                    </td>
                  </tr>
                  {e.service && (
                    <tr>
                      <td style={{ color: '#64748b', padding: '2px 0' }}>Service:</td>
                      <td style={{ color: '#1e3a8a', padding: '2px 0', fontWeight: '700' }}>{e.service}</td>
                    </tr>
                  )}
                  <tr>
                    <td style={{ color: '#64748b', padding: '2px 0' }}>Route (From &rarr; To):</td>
                    <td style={{ fontWeight: '700', color: '#2563eb', padding: '2px 0' }}>
                      {(e.road_from || e.pickup_address || '-')} &rarr; {(e.road_to || e.delivery_at || e.road_location || '-')}
                    </td>
                  </tr>
                  {(e.road_vehicle_type || e.road_vehicle_size) && (
                    <tr>
                      <td style={{ color: '#64748b', padding: '2px 0' }}>Vehicle Type &amp; Size:</td>
                      <td style={{ color: '#0f172a', padding: '2px 0', fontWeight: '600' }}>
                        {[e.road_vehicle_type, e.road_vehicle_size].filter(Boolean).join(' • ')}
                      </td>
                    </tr>
                  )}
                  {e.road_tat && (
                    <tr>
                      <td style={{ color: '#64748b', padding: '2px 0' }}>Transit Time (TAT):</td>
                      <td style={{ color: '#0f172a', padding: '2px 0', fontWeight: '600' }}>{formatTat(e.road_tat)}</td>
                    </tr>
                  )}
                  {(e.commodity || e.road_material_type) && (
                    <tr>
                      <td style={{ color: '#64748b', padding: '2px 0' }}>Cargo / Commodity:</td>
                      <td style={{ color: '#0f172a', padding: '2px 0', fontWeight: '600' }}>
                        {[e.commodity, e.road_material_type].filter(Boolean).join(' / ')}
                      </td>
                    </tr>
                  )}
                  {e.road_load_type === 'PTL' && e.road_weight_slab && (
                    <tr>
                      <td style={{ color: '#64748b', padding: '2px 0' }}>Weight Slab / Rate:</td>
                      <td style={{ color: '#0f172a', padding: '2px 0', fontWeight: '600' }}>
                        {e.road_weight_slab} {e.road_rate_per_kg ? `(@ ₹${e.road_rate_per_kg}/kg)` : ''}
                      </td>
                    </tr>
                  )}
                </>
              ) : (
                <>
                  <tr>
                    <td style={{ color: '#64748b', padding: '2px 0', width: '42%' }}>Scope / Movement:</td>
                    <td style={{ fontWeight: '700', color: '#0f172a', padding: '2px 0' }}>
                      {e.shipment_scope || 'INTERNATIONAL'} • {e.movement_direction || 'INWARD'}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ color: '#64748b', padding: '2px 0' }}>Validity Date:</td>
                    <td style={{ fontWeight: '700', color: '#0f172a', padding: '2px 0' }}>
                      {getValidityDate(e)}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ color: '#64748b', padding: '2px 0' }}>Transport Mode:</td>
                    <td style={{ fontWeight: '700', color: '#2563eb', padding: '2px 0' }}>
                      {e.mode_of_transport || 'AIR'} {e.cargo_type ? `(${e.cargo_type})` : ''}
                    </td>
                  </tr>
                  {e.service && (
                    <tr>
                      <td style={{ color: '#64748b', padding: '2px 0' }}>Service Type:</td>
                      <td style={{ color: '#0f172a', padding: '2px 0', fontWeight: '600' }}>{e.service}</td>
                    </tr>
                  )}
                  {e.term && (
                    <tr>
                      <td style={{ color: '#64748b', padding: '2px 0' }}>{e.shipment_scope === 'DOMESTIC' ? 'Freight Term:' : 'Incoterm:'}</td>
                      <td style={{ color: '#0f172a', padding: '2px 0', fontWeight: '700' }}>{e.term || (e.shipment_scope === 'DOMESTIC' ? 'PAID' : 'EXW')}</td>
                    </tr>
                  )}
                  {(e.aol || e.road_from) && (
                    <tr>
                      <td style={{ color: '#64748b', padding: '2px 0' }}>
                        {e.mode_of_transport === 'ROAD' ? 'Origin / From:' : 'AOL (Port of Loading):'}
                      </td>
                      <td style={{ color: '#0f172a', padding: '2px 0', fontWeight: '600' }}>
                        {e.aol || e.road_from}
                      </td>
                    </tr>
                  )}
                  {(e.aod || e.road_to || e.delivery_at) && (
                    <tr>
                      <td style={{ color: '#64748b', padding: '2px 0' }}>
                        {e.mode_of_transport === 'ROAD' ? 'Destination / To:' : 'AOD / Delivery At:'}
                      </td>
                      <td style={{ color: '#0f172a', padding: '2px 0', fontWeight: '600' }}>
                        {[e.aod, e.delivery_at, e.road_to].filter(Boolean).join(' / ')}
                      </td>
                    </tr>
                  )}
                  {e.commodity && (
                    <tr>
                      <td style={{ color: '#64748b', padding: '2px 0' }}>Commodity:</td>
                      <td style={{ color: '#0f172a', padding: '2px 0', fontWeight: '600' }}>{e.commodity}</td>
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── 3. Subject Banner (13px Uniform) ─────────────────────────── */}
      {e.subject && (
        <div style={{
          background: '#eff6ff',
          borderLeft: '4px solid #2563eb',
          padding: '7px 12px',
          marginBottom: '14px',
          fontSize: '12px',
          color: '#1e3a8a'
        }}>
          <strong>Subject: </strong>{e.subject}
        </div>
      )}

      {/* ── 4. Package & Dimensions Summary (13px Uniform) ──────────── */}
      {dimensionRows.length > 0 && !isFCL && (
        <div className="avoid-break" style={{ marginBottom: '14px', fontSize: '12px' }}>
          <div style={{
            fontSize: '12px',
            fontWeight: '700',
            color: '#1e3a8a',
            textTransform: 'uppercase',
            marginBottom: '4px',
            letterSpacing: '0.3px'
          }}>
            {(isRoadRail || dimensionRows.some(d => d.package_type)) ? 'Cargo Packages & Dimensions' : `Package & Cargo Dimensions (${e.dimension_unit || 'CM'})`}
          </div>
          <table className="table-bordered" style={{ fontSize: '12px' }}>
            <thead>
              <tr>
                <th style={{ width: '40px', textAlign: 'center' }}>#</th>
                {(isRoadRail || dimensionRows.some(d => d.package_type)) && (
                  <th style={{ width: '120px', textAlign: 'left' }}>Package Type</th>
                )}
                <th style={{ width: '90px', textAlign: 'center' }}>Packages</th>
                {(isRoadRail || dimensionRows.some(d => d.gross_weight)) && (
                  <th style={{ width: '110px', textAlign: 'center' }}>Gross Wt (kg)</th>
                )}
                <th style={{ textAlign: 'left' }}>Dimensions (L × W × H in {e.dimension_unit || 'CM'})</th>
                <th style={{ width: '100px', textAlign: 'center' }}>CBM (m³)</th>
                {e.mode_of_transport === 'AIR' && (
                  <th style={{ width: '120px', textAlign: 'right' }}>Vol. Wt (KGS)</th>
                )}
              </tr>
            </thead>
            <tbody>
              {dimensionRows.map((d, i) => {
                const l = parseFloat(d.length) || 0;
                const w = parseFloat(d.width) || 0;
                const h = parseFloat(d.height) || 0;
                const pkgs = d.packages || d.no_of_packages || d.boxes || '1';
                const rowUnit = d.unit || e.dimension_unit || 'CM';
                const dimStr = l && w && h ? `${l} × ${w} × ${h} ${rowUnit}` : '-';
                const hasPkgType = (isRoadRail || dimensionRows.some(x => x.package_type));
                const hasRowGross = (isRoadRail || dimensionRows.some(x => x.gross_weight));

                return (
                  <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                    <td style={{ textAlign: 'center', color: '#64748b' }}>{i + 1}</td>
                    {hasPkgType && (
                      <td style={{ textAlign: 'left', fontWeight: '600' }}>{d.package_type || 'Boxes'}</td>
                    )}
                    <td style={{ textAlign: 'center', fontWeight: '600' }}>{pkgs}</td>
                    {hasRowGross && (
                      <td style={{ textAlign: 'center' }}>{d.gross_weight ? `${fmtNum(d.gross_weight)} kg` : '-'}</td>
                    )}
                    <td style={{ textAlign: 'left', fontWeight: '500' }}>{dimStr}</td>
                    <td style={{ textAlign: 'center', color: '#047857', fontWeight: '600' }}>{d.cbm ? `${fmtNum(d.cbm, 4)} m³` : '-'}</td>
                    {e.mode_of_transport === 'AIR' && (
                      <td style={{ textAlign: 'right', fontWeight: '600' }}>{fmtNum(d.volume_weight)}</td>
                    )}
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ background: '#f1f5f9', fontWeight: '700' }}>
                <td colSpan={(isRoadRail || dimensionRows.some(d => d.package_type)) ? 2 : 1} style={{ textAlign: 'right', textTransform: 'uppercase', fontSize: '12px' }}>
                  Total Package Summary:
                </td>
                <td style={{ textAlign: 'center', fontSize: '12px' }}>
                  {dimensionRows.reduce((s, r) => s + (parseInt(r.packages || r.no_of_packages || r.boxes, 10) || 0), 0)} Pkgs
                </td>
                {(isRoadRail || dimensionRows.some(d => d.gross_weight)) && (
                  <td style={{ textAlign: 'center', fontSize: '12px' }}>
                    {(() => {
                      const sumGross = dimensionRows.reduce((sum, d) => sum + (parseFloat(d.gross_weight) || 0), 0);
                      const totalG = sumGross > 0 ? sumGross : (parseFloat(e.gross_weight) || 0);
                      return totalG > 0 ? `${fmtNum(totalG)} kg` : '-';
                    })()}
                  </td>
                )}
                <td style={{ textAlign: 'right', fontSize: '12px' }}></td>
                <td style={{ textAlign: 'center', color: '#047857', fontSize: '12px' }}>
                  {e.total_cbm ? `${fmtNum(e.total_cbm, 4)} m³` : '-'}
                </td>
                {e.mode_of_transport === 'AIR' && (
                  <td style={{ textAlign: 'right', fontSize: '12px' }}>
                    {fmtNum(e.total_volume_weight)} KGS
                  </td>
                )}
              </tr>
            </tfoot>
          </table>

          {/* Key Weight Badges Bar (13px Uniform) */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '16px',
            marginTop: '6px',
            fontSize: '12px',
            color: '#475569'
          }}>
            {e.total_cbm > 0 && <div>Total CBM: <strong style={{ color: '#0f172a' }}>{fmtNum(e.total_cbm, 3)} m³</strong></div>}
            {e.gross_weight > 0 && <div>Gross Wt: <strong style={{ color: '#0f172a' }}>{fmtNum(e.gross_weight)} KGS</strong></div>}
            {e.total_volume_weight > 0 && <div>Volume Wt: <strong style={{ color: '#0f172a' }}>{fmtNum(e.total_volume_weight)} KGS</strong></div>}
            {e.chargeable_weight > 0 && (
              <div style={{
                background: '#eff6ff',
                padding: '2px 8px',
                borderRadius: '4px',
                border: '1px solid #bfdbfe',
                color: '#1e3a8a',
                fontWeight: '700'
              }}>
                Chargeable Wt: {fmtNum(e.chargeable_weight)} KGS
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── 5. SEA FCL Container Grid (13px Uniform) ──────────────────── */}
      {isFCL && (e.containers || []).length > 0 && (
        <div className="avoid-break" style={{ marginBottom: '14px', fontSize: '12px' }}>
          <div style={{ fontSize: '12px', fontWeight: '700', color: '#1e3a8a', textTransform: 'uppercase', marginBottom: '4px' }}>
            Container Specifications (SEA FCL)
          </div>
          <table className="table-bordered" style={{ fontSize: '12px' }}>
            <thead>
              <tr>
                <th style={{ width: '40px', textAlign: 'center' }}>#</th>
                <th>Container Type</th>
                <th style={{ textAlign: 'right' }}>General Count</th>
                <th style={{ textAlign: 'right' }}>Reefer Count</th>
                <th style={{ textAlign: 'right' }}>Total Units</th>
              </tr>
            </thead>
            <tbody>
              {e.containers.map((c, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                  <td style={{ textAlign: 'center', color: '#64748b' }}>{i + 1}</td>
                  <td style={{ fontWeight: '600' }}>{c.container_type}</td>
                  <td style={{ textAlign: 'right' }}>{c.general_count || 0}</td>
                  <td style={{ textAlign: 'right' }}>{c.reefer_count || 0}</td>
                  <td style={{ textAlign: 'right', fontWeight: '700' }}>
                    {(parseInt(c.general_count, 10) || 0) + (parseInt(c.reefer_count, 10) || 0)}
                  </td>
                </tr>
              ))}
            </tbody>
            {e.sea_gross_weight > 0 && (
              <tfoot>
                <tr style={{ background: '#f1f5f9', fontWeight: '700' }}>
                  <td colSpan={4} style={{ textAlign: 'right' }}>Total FCL Gross Weight:</td>
                  <td style={{ textAlign: 'right' }}>{fmtNum(e.sea_gross_weight)} KGS</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {/* ── 6. Quotation Charge Breakdown ── */}
      {isLogisticsMode ? (
        /* ── Commercial Quotation Breakdown (Matching Screenshot) ── */
        <div className="avoid-break" style={{ marginBottom: '16px', fontSize: '12px' }}>
          <div style={{
            background: '#1e3a8a',
            color: '#fff',
            padding: '7px 12px',
            borderTopLeftRadius: '4px',
            borderTopRightRadius: '4px',
            fontWeight: '800',
            fontSize: '12px',
            letterSpacing: '0.5px',
            textTransform: 'uppercase'
          }}>
            COMMERCIAL QUOTATION BREAKDOWN
          </div>

          <table className="table-bordered" style={{ fontSize: '12px' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                <th style={{ width: '55px', textAlign: 'center', padding: '6px 8px' }}>Sl. No.</th>
                <th style={{ textAlign: 'left', padding: '6px 12px' }}>Description of Services / Charges</th>
                <th style={{ textAlign: 'center', width: '130px', padding: '6px 8px' }}>Rate Type</th>
                <th style={{ textAlign: 'right', width: '140px', padding: '6px 12px' }}>Amount (INR)</th>
              </tr>
            </thead>
            <tbody>
              {logisticsItems.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ textAlign: 'center', color: '#94a3b8', fontStyle: 'italic', padding: '12px' }}>
                    No charges specified
                  </td>
                </tr>
              ) : (
                logisticsItems.map((it, iIdx) => (
                  <tr key={it.id || iIdx} style={{ background: iIdx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                    <td style={{ textAlign: 'center', color: '#475569', fontWeight: '600', padding: '6px 8px' }}>
                      {iIdx + 1}
                    </td>
                    <td style={{ fontWeight: '500', color: '#0f172a', padding: '6px 12px' }}>
                      {it.description || '-'}
                    </td>
                    <td style={{ textAlign: 'center', color: '#334155', fontWeight: '600', padding: '6px 8px' }}>
                      {it.tariff || it.qty_unit || 'Per Trip'}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: '700', color: '#0f172a', padding: '6px 12px' }}>
                      {fmtAmt(it.amount || it.total, currSymbol)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Bottom-Right Summary Calculation Box (Exact Match to Screenshot) */}
          <div style={{
            marginTop: '10px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            width: '100%'
          }}>
            <div style={{ width: '380px', maxWidth: '100%' }}>
              <table className="table-bordered" style={{ fontSize: '12px' }}>
                <tbody>
                  <tr style={{ background: '#fff' }}>
                    <td style={{ border: '1px solid #94a3b8', padding: '6px 12px', fontWeight: '600', color: '#334155' }}>
                      Sub-Total:
                    </td>
                    <td style={{ border: '1px solid #94a3b8', padding: '6px 12px', textAlign: 'right', fontWeight: '700', color: '#0f172a' }}>
                      {fmtAmt(logSubTotal, currSymbol)}
                    </td>
                  </tr>
                  <tr style={{ background: '#f8fafc' }}>
                    <td style={{ border: '1px solid #94a3b8', padding: '6px 12px', fontWeight: '600', color: '#334155' }}>
                      {logGstLabel}:
                    </td>
                    <td style={{ border: '1px solid #94a3b8', padding: '6px 12px', textAlign: 'right', fontWeight: '700', color: '#0f172a' }}>
                      {fmtAmt(logGstAmount, currSymbol)}
                    </td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr style={{ background: '#eff6ff', borderTop: '2px solid #1e3a8a' }}>
                    <td style={{ border: '1px solid #94a3b8', padding: '8px 12px', fontWeight: '800', fontSize: '12px', textTransform: 'uppercase', color: '#1e3a8a' }}>
                      Total Estimated Payable:
                    </td>
                    <td style={{ border: '1px solid #94a3b8', padding: '8px 12px', fontWeight: '900', fontSize: '13px', textAlign: 'right', color: '#1e3a8a' }}>
                      {fmtAmt(logTotalPayable, currSymbol)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {logTotalPayable > 0 && (
              <div style={{ marginTop: '6px', fontSize: '12px', color: '#334155', fontStyle: 'italic', textAlign: 'right', width: '100%' }}>
                <strong>Amount in Words: </strong> {numberToWordsINR(logTotalPayable, e.currency || 'INR')}
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
        {/* ── 6. Quotation Charge Groups Breakdown (13px Uniform, 16px Headers & Subtotals) ── */}
      {(e.charge_groups || []).map((grp, gIdx) => {
        const isAirFreightGroup = grp.group_label === 'B' || (grp.group_name || '').toUpperCase().includes('AIR FREIGHT');
        return (
          <div key={grp.id || gIdx} className="avoid-break" style={{ marginBottom: '14px', fontSize: '12px' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: '#1e3a8a',
              color: '#fff',
              padding: '6px 10px',
              borderTopLeftRadius: '4px',
              borderTopRightRadius: '4px',
              fontWeight: '700',
              fontSize: '12px',
              letterSpacing: '0.4px'
            }}>
              <span>GROUP {grp.group_label}: {grp.group_name || 'Charges'}</span>
              <span style={{ fontSize: '12px', fontWeight: '500', opacity: 0.9 }}>
                Subtotal: {fmtAmt(grp.group_total, currSymbol)}
              </span>
            </div>

            <table className="table-bordered" style={{ fontSize: '12px' }}>
              <thead>
                <tr>
                  <th style={{ width: '40px', textAlign: 'center' }}>#</th>
                  <th style={{ textAlign: 'left' }}>Description</th>
                  {isAirFreightGroup && <th style={{ textAlign: 'left', width: '130px' }}>Airline / Vendor</th>}
                  {isAirFreightGroup && <th style={{ textAlign: 'left', width: '100px' }}>Tariff</th>}
                  <th style={{ textAlign: 'right', width: '110px' }}>Rate / Amount</th>
                  <th style={{ textAlign: 'center', width: '70px' }}>Qty</th>
                  <th style={{ textAlign: 'center', width: '80px' }}>Unit</th>
                  <th style={{ textAlign: 'right', width: '130px' }}>Total Amount ({e.currency || 'INR'})</th>
                </tr>
              </thead>
              <tbody>
                {(grp.items || []).length === 0 ? (
                  <tr>
                    <td colSpan={isAirFreightGroup ? 7 : 5} style={{ textAlign: 'center', color: '#94a3b8', fontStyle: 'italic', padding: '10px' }}>
                      No charge items in this group
                    </td>
                  </tr>
                ) : (
                  grp.items.map((it, iIdx) => (
                    <tr key={it.id || iIdx} style={{ background: iIdx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                      <td style={{ textAlign: 'center', color: '#64748b' }}>{iIdx + 1}</td>
                      <td style={{ fontWeight: '500', color: '#0f172a' }}>{it.description || '-'}</td>
                      {isAirFreightGroup && <td style={{ color: '#475569' }}>{it.airline_vendor || '-'}</td>}
                      {isAirFreightGroup && <td style={{ color: '#475569' }}>{it.tariff || '-'}</td>}
                      <td style={{ textAlign: 'right' }}>{fmtAmt(it.amount, currSymbol)}</td>
                      <td style={{ textAlign: 'center' }}>{it.qty || 1}</td>
                      <td style={{ textAlign: 'center', color: '#64748b' }}>{it.qty_unit || '-'}</td>
                      <td style={{ textAlign: 'right', fontWeight: '600' }}>{fmtAmt(it.total, currSymbol)}</td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot>
                <tr style={{ background: '#f8fafc', fontWeight: '700' }}>
                  <td colSpan={isAirFreightGroup ? 6 : 4} style={{ border: '1px solid #94a3b8', textAlign: 'right', textTransform: 'uppercase', color: '#1e3a8a', fontSize: '12px' }}>
                    Subtotal (Group {grp.group_label}):
                  </td>
                  <td style={{ border: '1px solid #94a3b8', textAlign: 'right', color: '#1e3a8a', fontSize: '12px', fontWeight: '800' }}>
                    {fmtAmt(grp.group_total, currSymbol)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        );
      })}

      {/* ── 7. Consolidated Summary of Quotation (Shifted to Right Side) ── */}
      <div className="avoid-break" style={{
        marginTop: '12px',
        marginBottom: '16px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        width: '100%'
      }}>
        <div style={{ width: '420px', maxWidth: '100%' }}>
          <table className="table-bordered" style={{ fontSize: '12px' }}>
            <thead>
              <tr style={{ background: '#0f172a', color: '#fff' }}>
                <th colSpan={2} style={{ textAlign: 'left', padding: '6px 10px', fontSize: '12px', letterSpacing: '0.4px', background: '#0f172a', color: '#fff' }}>
                  CONSOLIDATED SUMMARY OF QUOTATION
                </th>
              </tr>
            </thead>
            <tbody>
              {(e.charge_groups || []).map((g, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                  <td style={{ border: '1px solid #94a3b8', padding: '6px 10px', color: '#334155', fontSize: '12px' }}>
                    Group {g.group_label}: {g.group_name}
                  </td>
                  <td style={{ border: '1px solid #94a3b8', padding: '6px 10px', textAlign: 'right', fontWeight: '600', fontSize: '12px' }}>
                    {fmtAmt(g.group_total, currSymbol)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ background: '#eff6ff', borderTop: '2px solid #1e3a8a' }}>
                <td style={{ border: '1px solid #94a3b8', padding: '8px 10px', fontWeight: '800', fontSize: '12px', textTransform: 'uppercase', color: '#1e3a8a' }}>
                  Grand Total ({e.currency || 'INR'})
                </td>
                <td style={{ border: '1px solid #94a3b8', padding: '8px 10px', fontWeight: '900', fontSize: '12px', textAlign: 'right', color: '#1e3a8a' }}>
                  {fmtAmt(grandTotal, currSymbol)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        {grandTotal > 0 && (
          <div style={{ marginTop: '6px', fontSize: '12px', color: '#334155', fontStyle: 'italic', textAlign: 'right', width: '100%' }}>
            <strong>Amount in Words: </strong> {numberToWordsINR(grandTotal, e.currency || 'INR')}
          </div>
        )}
      </div>

      
        </>
      )}

      {/* ── 8. Highlight Notes (11px Uniform) ────────────────────────── */}
      {e.highlight_notes && (
        <div className="avoid-break" style={{
          border: '1px solid #fed7aa',
          borderRadius: '6px',
          background: '#fffaf5',
          padding: '10px 14px',
          marginBottom: '14px',
          fontSize: '12px'
        }}>
          <div style={{ fontWeight: '700', color: '#c2410c', textTransform: 'uppercase', marginBottom: '5px', fontSize: '12px' }}>
            Important Quotation Notes &amp; Assumptions
          </div>
          <div style={{ whiteSpace: 'pre-line', color: '#9a3412', lineHeight: 1.45, fontWeight: '500' }}>
            {e.highlight_notes}
          </div>
        </div>
      )}

      {/* ── 9. Remarks Numbered List (13px Uniform) ──────────────────── */}
      {remarksList.length > 0 && (
        <div className="avoid-break" style={{ marginBottom: '16px', fontSize: '12px' }}>
          <div style={{ fontWeight: '700', color: '#0f172a', marginBottom: '4px', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.4px' }}>
            Terms &amp; Conditions / Standard Remarks:
          </div>
          <ol style={{ paddingLeft: '20px', margin: 0, lineHeight: '1.6', color: '#334155', fontSize: '12px' }}>
            {remarksList.map((r, i) => (
              <li key={i} style={{ marginBottom: '3px' }}>{r}</li>
            ))}
          </ol>
        </div>
      )}

      {/* ── 10. Signature Blocks (Customer Acceptance + Company Signatory - 13px) ── */}
      <div className="avoid-break" style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '24px',
        marginTop: '22px',
        paddingTop: '14px',
        borderTop: '1px dashed #cbd5e1'
      }}>
        {/* Customer Acceptance */}
        <div style={{ border: '1px solid #e2e8f0', borderRadius: '6px', padding: '12px', background: '#fafafa' }}>
          <div style={{ fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Customer Confirmation &amp; Acceptance
          </div>
          <div style={{ fontSize: '12px', color: '#64748b', marginTop: '30px' }}>
            Authorized Signature &amp; Company Stamp
          </div>
          <div style={{ marginTop: '8px', borderTop: '1px solid #cbd5e1', paddingTop: '3px', fontSize: '12px', color: '#64748b' }}>
            Date: ________________________
          </div>
        </div>

        {/* Company Signatory */}
        <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div>
            {(e.signature_text || `Thanking you in advance.\n\nFor, ${cmpName}`)
              .split('\n')
              .map((line, lIdx) => {
                const trimmed = line.trim();
                if (!trimmed) return <div key={lIdx} style={{ height: '8px' }} />;
                const isForCompany = trimmed.toLowerCase().startsWith('for,') || trimmed.toLowerCase().startsWith('for ');
                const isAuth = trimmed.toLowerCase().includes('authorized signatory');
                return (
                  <div
                    key={lIdx}
                    style={{
                      fontSize: '12px',
                      fontWeight: isForCompany || isAuth ? '700' : '400',
                      color: isForCompany ? '#0f172a' : (isAuth ? '#0f172a' : '#475569'),
                      textTransform: isForCompany ? 'uppercase' : 'none',
                      marginTop: isForCompany ? '2px' : '0'
                    }}
                  >
                    {line}
                  </div>
                );
              })}
          </div>
          <div style={{ marginTop: '30px' }}>
            {!((e.signature_text || '').toLowerCase().includes('authorized signatory')) && (
              <div style={{ fontSize: '12px', fontWeight: '700', color: '#0f172a' }}>(Authorized Signatory)</div>
            )}
            <div style={{ fontSize: '12px', color: '#94a3b8' }}>This is a computer generated quote</div>
          </div>
        </div>
      </div>

      {/* ── 11. Official Footer ─────────────────────────────────────── */}
      <div style={{
        marginTop: '20px',
        paddingTop: '8px',
        borderTop: '1px solid #e2e8f0',
        textAlign: 'center',
        fontSize: '12px',
        color: '#64748b',
        lineHeight: '1.45'
      }}>
        <div style={{ fontWeight: '700', color: '#334155', fontSize: '12.5px' }}>{cmpName}</div>
        <div>{cmpAddr}</div>
        <div><strong style={{ color: '#0f172a' }}>GSTIN:</strong> {cmpGst} {cmpPan ? <>• <strong style={{ color: '#0f172a' }}>PAN:</strong> {cmpPan}</> : ''} • <strong style={{ color: '#0f172a' }}>Email:</strong> {cmpEmail} • <strong style={{ color: '#0f172a' }}>Web:</strong> {cmpWeb}</div>
      </div>

    </div>
  );
}
